package com.nonopn.skydome.common.app.drawer

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material.Divider
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.rounded.AccountBox
import androidx.compose.material.ripple
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.unit.dp
import com.nonopn.icons.MapLocation
import com.nonopn.skydome.common.app.home.HotPreviewApp
import com.nonopn.skydome.common.app.navigation.RouterMap
import com.nonopn.skywatcher.common.login.pages.RouterMain
import com.nonopn.skywatcher.design.local.LocalFontSizes
import com.nonopn.skywatcher.design.theme.AppColor
import de.drick.compose.hotpreview.HotPreview
import eu.codlab.compose.widgets.CustomOutlinedButton
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.navigation.RouteParameterTo
import eu.codlab.navigation.Router

@Suppress("MagicNumber")
@Composable
fun DrawerSeparator(
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .height(8.dp)
            .fillMaxWidth()
            .systemBackground()
            .padding(start = 12.dp, end = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Divider(
            modifier = Modifier
                .height(1.dp)
                .systemBackground(),
            color = colorDimmed()
        )
    }
}

@Suppress("MagicNumber")
@Composable
fun DrawerTitle(
    modifier: Modifier = Modifier,
    text: String,
    selected: Boolean = false
) {
    Row(
        modifier = modifier
            .height(32.dp)
            .fillMaxWidth()
            .systemBackground(selected = selected)
            .padding(start = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        TextNormal(
            fontSize = LocalFontSizes.current.menu.title,
            text = text,
            fontFamily = FontFamily.SansSerif,
            color = colorDimmed()
        )
    }
}

@Suppress("MagicNumber")
@Composable
fun DrawerItem(
    modifier: Modifier = Modifier,
    text: String,
    router: Router<*>?,
    currentRoute: RouteParameterTo? = null,
    image: ImageVector,
    tiny: Boolean = false,
    onClick: () -> Unit
) {
    val selected = router?.isCurrentRoute(currentRoute) ?: false
    val imageSize = 18.dp

    if (tiny) {
        Row(
            modifier = modifier
                .height(32.dp)
                .fillMaxWidth()
                .systemBackground(selected = selected)
                .padding(start = 8.dp)
                .clickable(
                    interactionSource = MutableInteractionSource(),
                    indication = null,
                    onClick = { onClick() }
                ),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Image(
                modifier = Modifier.width(imageSize).height(imageSize),
                imageVector = image,
                contentDescription = text,
                colorFilter = ColorFilter.tint(color(selected))
            )
        }
        return
    }

    Row(
        modifier = modifier
            .height(32.dp)
            .fillMaxWidth()
            .systemBackground(selected = selected)
            .clickable(
                interactionSource = MutableInteractionSource(),
                indication = ripple(),
                onClick = { onClick() }
            )
            .padding(0.dp)
            .padding(start = 32.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Image(
            modifier = Modifier.width(imageSize).height(imageSize),
            imageVector = image,
            contentDescription = text,
            colorFilter = ColorFilter.tint(color(selected))
        )

        TextNormal(
            fontSize = LocalFontSizes.current.menu.item,
            text = text,
            fontFamily = FontFamily.Default,
            color = color(selected)
        )
    }
}

@Suppress("MagicNumber")
@Composable
fun DrawerButton(
    modifier: Modifier = Modifier,
    text: String,
    image: ImageVector,
    tiny: Boolean = false,
    onClick: () -> Unit
) {
    if (tiny) {
        Row(
            modifier = modifier
                .height(32.dp)
                .fillMaxWidth()
                .padding(start = 8.dp)
                .clickable(
                    interactionSource = MutableInteractionSource(),
                    indication = null,
                    onClick = onClick
                ),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Image(
                imageVector = image,
                contentDescription = text,
                colorFilter = ColorFilter.tint(color(false))
            )
        }
        return
    }

    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        CustomOutlinedButton(
            modifier = Modifier.padding(8.dp),
            onClick = onClick
        ) {
            TextNormal(
                fontSize = LocalFontSizes.current.menu.switch,
                text = text
            )
        }
    }
}

@Suppress("MagicNumber")
@Composable
fun color(selected: Boolean = false): Color = if (selected) {
    AppColor.StrikeAccent
} else {
    AppColor.White
}

@Suppress("MagicNumber")
@Composable
fun colorDimmed() = AppColor.White.copy(alpha = 0.6f)

@Suppress("MagicNumber")
@Composable
fun Modifier.systemBackground(selected: Boolean = false) =
    if (selected) {
        this.background(AppColor.StrikeSidebarSelected)
    } else {
        this
    }

@HotPreview(widthDp = 200, heightDp = 50, darkMode = true)
@HotPreview(widthDp = 200, heightDp = 50, darkMode = false)
@Composable
private fun DrawerItemPreviewDark() {
    HotPreviewApp(isDarkTheme = isSystemInDarkTheme()) {
        Column(modifier = Modifier.fillMaxWidth()) {
            DrawerTitle(text = "Title")
            DrawerSeparator()
            DrawerItem(
                text = "Item",
                router = RouterMain,
                currentRoute = RouterMain.navigateTo().route,
                image = Icons.Rounded.MapLocation
            ) {
                // nothing
            }
            DrawerItem(
                text = "Item",
                router = RouterMain,
                currentRoute = RouterMain.navigateTo().route,
                image = Icons.Rounded.AccountBox
            ) {
                // nothing
            }

            DrawerItem(
                text = "Item",
                router = RouterMain,
                currentRoute = RouterMap.navigateTo().route,
                tiny = true,
                image = Icons.Rounded.MapLocation
            ) {
                // nothing
            }

            DrawerButton(
                image = Icons.Default.Refresh,
                text = "Test for button only"
            ) {
                // nothing
            }
        }
    }
}
